import React, { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Button, { ButtonProps } from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// Components
import Text from '../text';
import Space from '../layout/space';
// Resources
import { stringsRes } from '../../resources/strings';

interface BackButtonProps extends ButtonProps {
    label?: string;
    title?: string;
    spaceBottom?: number;
    buttonRight?: JSX.Element | JSX.Element[];
    backTo?: string;
}

const BackButton: FC<BackButtonProps> = ({
    label = stringsRes.common.back,
    title,
    spaceBottom = 4,
    buttonRight,
    backTo,
    ...rest
}): ReactElement => {
    const classes = useStyles();
    const navigate = useNavigate();

    const onAction = (): void => {
        backTo ? navigate(backTo) : navigate(-1);
    };

    let buttonView = (
        <Button
            id="backButton"
            onClick={onAction}
            startIcon={<ArrowBackIcon className={classes.icon} />}
            {...rest}
            className={classes.backButtonRoot}
        >
            <Text.ShortMediumText16 text={label} />
        </Button>
    );

    if (buttonRight) {
        buttonView = (
            <div className={classes.backButtonContainer}>
                <Button
                    id="backButton"
                    onClick={onAction}
                    startIcon={<ArrowBackIcon className={classes.icon} />}
                    {...rest}
                    className={classes.backButtonRoot}
                >
                    <Text.ShortMediumText16 text={label ?? stringsRes.common.back} />
                </Button>
                <div className={classes.backButtonRightContainer}>{buttonRight}</div>
            </div>
        );
    }

    return (
        <>
            {buttonView}
            {title && <Space value={1.5} />}
            {title && <Text.H0 text={title} />}
            {spaceBottom && <Space value={spaceBottom} />}
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButtonRoot: {
            height: '30px',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
            padding: 0,
            width: 'inherit',
            color: theme.palette.primary.main,
            '&:hover': {
                background: 'unset',
            },
        },
        icon: {
            color: theme.palette.colors.icon.primary,
        },
        backButtonContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        backButtonRightContainer: {
            display: 'flex',
        },
    }),
);

export default BackButton;
